import { merge, Observable, of } from 'rxjs'
import { Property } from './property'
import { filter, map, tap } from 'rxjs/operators'
import { Pageable } from '@elderbyte/ngx-starter'
import { PropertyFilter, PropertyService } from './property.service'
import { Injectable, inject } from '@angular/core'
import { LoggerFactory } from '@elderbyte/ts-logger'

/**
 * Not provided in root on purpose to have separate cache instances
 */
@Injectable()
export class PropertyCache {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly _cachedProperties = new Map<string, Property>()

  private propertyService = inject(PropertyService)

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  /**
   * Returns the cached properties if present or loads missing ones.
   * @param ids The ids of the properties to load.
   */
  public getProperties(ids: string[]): Observable<Property[]> {
    const cachedProps = ids
      .map((id) => this._cachedProperties.get(id))
      .filter((property) => !!property)

    const toLoadIds = ids.filter((id) => !this._cachedProperties.has(id))

    this.log.debug('Cached Properties: ' + cachedProps.map((p) => p.id) + '\tTo Load: ' + toLoadIds)

    if (toLoadIds?.length > 0) {
      return merge(this.loadProperties(toLoadIds), of(cachedProps))
    } else {
      return of(cachedProps)
    }
  }

  /**
   * Populates the property of the given objects. Only returns populated objects!
   * @param objects The objects whose property fields should be populated.
   * @param getPropertyId The function used to retrieve the propertyId from the object.
   * @param setProperty The function used to set the object's property.
   */
  public populateObjects(
    objects: any[],
    getPropertyId: (object: any) => string,
    setProperty: (object: any, property: Property) => void
  ): Observable<any[]> {
    const propIds = objects.map((object) => getPropertyId(object))
    return this.getProperties(propIds).pipe(
      filter((properties) => properties?.length > 0),
      map((properties) => {
        objects.forEach((object) => {
          const matchingProp = properties.find((property) => property.id === getPropertyId(object))
          if (!!matchingProp) {
            setProperty(object, matchingProp)
          }
        })
        return objects
      })
    )
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private loadProperties(ids: string[]): Observable<Property[]> {
    return this.propertyService.findAllPaged(new Pageable(0, 100), [PropertyFilter.ids(ids)]).pipe(
      map((page) => page.content),
      tap((properties) => this.cacheProperties(properties))
    )
  }

  private cacheProperties(props: Property[]): void {
    props.forEach((property) => this._cachedProperties.set(property.id, property))
  }
}
